// Feature Documentation: features/nma-v3-icd10-pipeline.md
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  VStack,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  SimpleGrid,
  Heading,
  HStack,
  Badge,
  Button,
  Divider,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardHeader,
  CardBody,
  Tooltip
} from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import HeaderMenus from '../HeaderMenus';
import CaseHeaderPanel from './CaseHeaderPanel';
import MedicalNecessityPanel from './MedicalNecessityPanel';

const CptLinePointersCard = ({ results, compareResults, claimsData, claimType, accentScheme = 'blue' }) => {
  if (!results) return null;

  const orderedDiagnoses = results.ordered_diagnoses || [];
  const lineLevelPointers = results.line_level_pointers || {};
  const contextCpts = results.context?.cpt_codes || [];

  // Map pointer letter (A, B, C...) -> diagnosis object
  const pointerToDx = {};
  orderedDiagnoses.forEach((dx, idx) => {
    const ptr = (dx.pointer || String.fromCharCode(65 + idx)).toUpperCase();
    pointerToDx[ptr] = dx;
  });

  // Map pointer letter in compareResults to detect differences
  const otherPointerToDx = {};
  (compareResults?.ordered_diagnoses || []).forEach((dx, idx) => {
    const ptr = (dx.pointer || String.fromCharCode(65 + idx)).toUpperCase();
    otherPointerToDx[ptr] = dx;
  });
  const otherLinePointers = compareResults?.line_level_pointers || {};

  // Map CPT metadata (units, modifiers) from claimsData[claimType]
  const cptMetaMap = {};
  const rawClaimCpts = claimsData?.[claimType]?.cpt_codes;
  if (Array.isArray(rawClaimCpts)) {
    rawClaimCpts.forEach((item) => {
      if (item && item.cpt_code) {
        cptMetaMap[item.cpt_code] = item;
      }
    });
  } else if (rawClaimCpts && typeof rawClaimCpts === 'object') {
    Object.entries(rawClaimCpts).forEach(([k, val]) => {
      cptMetaMap[k] = typeof val === 'object' ? { cpt_code: k, ...val } : { cpt_code: k };
    });
  }

  // Build ordered list of CPT keys to display
  const pointerCptKeys = Object.keys(lineLevelPointers);
  const cptList = pointerCptKeys.length > 0 ? pointerCptKeys : contextCpts;

  return (
    <Card variant="outline" borderColor={accentScheme === 'purple' ? 'purple.200' : 'gray.200'}>
      <CardHeader pb={2} bg={accentScheme === 'purple' ? 'purple.50' : 'gray.50'}>
        <HStack justify="space-between">
          <Heading size="sm" color={accentScheme === 'purple' ? 'purple.700' : 'gray.700'}>
            CPT Codes &amp; Line-Level Pointers (CMS-1500 Box 24E)
          </Heading>
          <Badge colorScheme={accentScheme} fontSize="xs">
            {cptList.length} CPT{cptList.length === 1 ? '' : 's'}
          </Badge>
        </HStack>
      </CardHeader>
      <CardBody pt={2}>
        {cptList.length === 0 ? (
          <Text fontSize="sm" color="gray.500">No CPT line-level pointers available.</Text>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>CPT Code</Th>
                  <Th>Mods / Units</Th>
                  <Th>Box 24E Pointers</Th>
                  <Th>Mapped Diagnoses (Pointer → ICD-10)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {cptList.map((cpt) => {
                  const pointers = lineLevelPointers[cpt] || lineLevelPointers[cpt?.slice(0, 5)] || lineLevelPointers.ALL_CPTS || [];
                  const otherPtrs = otherLinePointers[cpt] || otherLinePointers[cpt?.slice(0, 5)] || otherLinePointers.ALL_CPTS || [];
                  const resolvedCodes = pointers.map((p) => pointerToDx[p]?.code || p);
                  const otherResolvedCodes = otherPtrs.map((p) => otherPointerToDx[p]?.code || p);
                  const lineChanged = compareResults && resolvedCodes.join(',') !== otherResolvedCodes.join(',');

                  const meta = cptMetaMap[cpt] || cptMetaMap[cpt?.slice(0, 5)] || {};
                  const modifiers = Array.isArray(meta.modifiers)
                    ? meta.modifiers.join(', ')
                    : (meta.modifier || '');
                  const units = meta.units !== undefined && meta.units !== null ? `${meta.units}u` : '';

                  return (
                    <Tr key={cpt} bg={lineChanged ? (accentScheme === 'purple' ? 'purple.50' : 'blue.50') : 'transparent'}>
                      <Td fontWeight="bold" whiteSpace="nowrap">
                        {cpt === 'ALL_CPTS' ? 'All CPTs' : cpt}
                      </Td>
                      <Td whiteSpace="nowrap" fontSize="xs" color="gray.600">
                        {modifiers ? <Badge colorScheme="gray" mr={1}>{modifiers}</Badge> : null}
                        {units ? <Badge colorScheme="teal" variant="subtle">{units}</Badge> : null}
                        {!modifiers && !units && '-'}
                      </Td>
                      <Td whiteSpace="nowrap">
                        <HStack spacing={1}>
                          {pointers.length > 0 ? pointers.map((ptr) => (
                            <Badge
                              key={ptr}
                              colorScheme={ptr === 'A' ? accentScheme : 'gray'}
                              variant="solid"
                              fontSize="xs"
                              px={1.5}
                            >
                              {ptr}
                            </Badge>
                          )) : <Text fontSize="xs" color="gray.400">None</Text>}
                        </HStack>
                      </Td>
                      <Td>
                        <HStack spacing={1.5} flexWrap="wrap">
                          {pointers.map((ptr) => {
                            const dx = pointerToDx[ptr];
                            const codeStr = dx?.code || 'Unknown';
                            const descStr = dx?.description || '';
                            const otherCodeAtSamePtr = otherPointerToDx[ptr]?.code;
                            const isDiffCode = compareResults && otherCodeAtSamePtr !== codeStr;

                            return (
                              <Tooltip
                                key={ptr}
                                label={`${ptr}: ${codeStr}${descStr ? ` — ${descStr}` : ''}`}
                                placement="top"
                                hasArrow
                              >
                                <Badge
                                  colorScheme={ptr === 'A' ? (isDiffCode ? 'green' : accentScheme) : (isDiffCode ? 'purple' : 'gray')}
                                  variant={ptr === 'A' || isDiffCode ? 'subtle' : 'outline'}
                                  fontSize="xs"
                                  px={2}
                                  py={0.5}
                                  cursor="help"
                                >
                                  {ptr}: {codeStr}
                                </Badge>
                              </Tooltip>
                            );
                          })}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
        {results.mapping_rationale && (
          <Text fontSize="xs" color="gray.600" mt={2}>
            <strong>Pointer Mapping Rationale:</strong> {results.mapping_rationale}
          </Text>
        )}
      </CardBody>
    </Card>
  );
};

const NMAV2Compare = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);

  const [claimType, setClaimType] = useState('pro');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [caseData, setCaseData] = useState(null);
  const [claimsData, setClaimsData] = useState(null);
  const [v1Results, setV1Results] = useState(null);
  const [v3Results, setV3Results] = useState(null);
  const [runningV3, setRunningV3] = useState(false);

  const [comparisonData, setComparisonData] = useState(null);
  const [generatingComparison, setGeneratingComparison] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!caseId || !accessToken) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Fetch Case Details for the Header
      const detailsResponse = await callApi(`/get_case_details/${caseId}`, accessToken);
      if (detailsResponse.ok) {
        const detailsData = await detailsResponse.json();
        setCaseData(detailsData);
      } else {
        throw new Error('Failed to fetch case details');
      }

      // 2. Fetch Current (Advanced / V1) Results + Claims CPT Data
      const v1Response = await callApi(`/nma/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=advanced`, accessToken, { method: 'GET' });
      if (v1Response.ok) {
        const v1Data = await v1Response.json();
        if (v1Data.status === 'success') {
          setV1Results(v1Data.results);
          if (v1Data.claims) {
            setClaimsData(v1Data.claims);
          }
        }
      }

      // 3. Fetch V3 Results
      const v3Response = await callApi(`/nma/analyze_case/${caseId}?claim_type=${claimType}&pipeline_version=v3`, accessToken, { method: 'GET' });
      if (v3Response.ok) {
        const v3Data = await v3Response.json();
        if (v3Data.status === 'success') {
          setV3Results(v3Data.results);
          if (v3Data.claims) {
            setClaimsData(v3Data.claims);
          }
        }
      }

      // 4. Fetch Existing V3 Comparison (if any)
      const compResponse = await callApi(`/nma/v3/compare_runs/${caseId}?claim_type=${claimType}`, accessToken, { method: 'GET' });
      if (compResponse.ok) {
        const compData = await compResponse.json();
        if (compData.status === 'success') {
          setComparisonData(compData);
        } else {
          setComparisonData(null);
        }
      }

    } catch (err) {
      console.error("Error fetching comparison data:", err);
      setError(err.message || 'An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  }, [caseId, claimType, accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRunV3Analysis = async () => {
    setRunningV3(true);
    setComparisonError(null);
    try {
      const response = await callApi('/nma/analyze_case', accessToken, {
        method: 'POST',
        body: JSON.stringify({
          external_case_id: caseId,
          claim_type: claimType,
          pipeline_version: 'v3',
          clean_phi: true
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.results) {
          setV3Results(data.results);
        } else {
          setComparisonError(data.message || 'Failed to run V3 analysis.');
        }
      } else {
        setComparisonError('Server error while running V3 analysis.');
      }
    } catch (err) {
      setComparisonError(err.message || 'Error running V3 analysis.');
    } finally {
      setRunningV3(false);
    }
  };

  const handleGenerateComparison = async () => {
    setGeneratingComparison(true);
    setComparisonError(null);
    try {
      const response = await callApi('/nma/v3/compare_runs', accessToken, {
        method: 'POST',
        body: JSON.stringify({ external_case_id: caseId, claim_type: claimType })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          setComparisonData(data);
        } else {
          setComparisonError(data.error_message || 'Failed to generate comparison.');
        }
      } else {
        setComparisonError('Server error while generating comparison.');
      }
    } catch (err) {
      setComparisonError(err.message || 'An error occurred.');
    } finally {
      setGeneratingComparison(false);
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading V1 vs V3 comparison data...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  const v1Codes = v1Results?.ordered_diagnoses?.map(d => d.code) || [];
  const v3Codes = v3Results?.ordered_diagnoses?.map(d => d.code) || [];
  const sharedCodes = v1Codes.filter(code => v3Codes.includes(code));
  const v1PointerA = v1Codes[0] || 'None';
  const v3PointerA = v3Codes[0] || 'None';
  const pointerAChanged = v1Codes.length > 0 && v3Codes.length > 0 && v1PointerA !== v3PointerA;

  const llmAnalysis = comparisonData?.llm_analysis;
  const preferenceScore = llmAnalysis?.v3_preference_score ?? llmAnalysis?.v2_preference_score;
  const v3Eval = llmAnalysis?.v3_evaluation || llmAnalysis?.v2_evaluation;

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />

      <Box maxW="1600px" mx="auto" p={4}>
        <VStack spacing={6} align="stretch">

          <HStack justify="space-between" align="center" flexWrap="wrap">
            <Heading size="lg" color="gray.700">NMA Pipeline Comparison (Advanced vs V3)</Heading>
            <HStack spacing={3}>
              <Select
                size="sm"
                value={claimType}
                onChange={(e) => setClaimType(e.target.value)}
                width="150px"
                bg="white"
              >
                <option value="pro">Pro Claim</option>
                <option value="tech">Tech Claim</option>
              </Select>
              <Badge colorScheme="purple" p={2} borderRadius="md">V1 Advanced vs V3 A/B Testing</Badge>
            </HStack>
          </HStack>

          {caseData && (
            <CaseHeaderPanel caseData={caseData} />
          )}

          {/* Pointer A & Sequence Diff Strip */}
          {(v1Codes.length > 0 || v3Codes.length > 0) && (
            <Box bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor={pointerAChanged ? 'purple.300' : 'gray.200'}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} alignItems="center">
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Current (Advanced) Sequence (A → F)</Text>
                  <HStack spacing={2} mt={1} flexWrap="wrap">
                    {v1Codes.length > 0 ? v1Codes.map((c, idx) => (
                      <Badge key={`${c}-${idx}`} colorScheme={idx === 0 ? 'blue' : 'gray'} fontSize="sm" px={2} py={0.5}>
                        {String.fromCharCode(65 + idx)}: {c}
                      </Badge>
                    )) : <Text fontSize="sm" color="gray.400">Not Run</Text>}
                  </HStack>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="purple.600" textTransform="uppercase">V3 Sequence (A → F)</Text>
                  <HStack spacing={2} mt={1} flexWrap="wrap">
                    {v3Codes.length > 0 ? v3Codes.map((c, idx) => (
                      <Badge key={`${c}-${idx}`} colorScheme={idx === 0 ? 'purple' : 'gray'} fontSize="sm" px={2} py={0.5}>
                        {String.fromCharCode(65 + idx)}: {c}
                      </Badge>
                    )) : <Text fontSize="sm" color="gray.400">Not Run</Text>}
                  </HStack>
                </Box>
                <Box textAlign={{ base: 'left', md: 'right' }}>
                  {pointerAChanged ? (
                    <Badge colorScheme="green" fontSize="sm" p={2} borderRadius="md">
                      Pointer A Updated: {v1PointerA} → {v3PointerA}
                    </Badge>
                  ) : (v1Codes.length > 0 && v3Codes.length > 0 && v1Codes.join(',') !== v3Codes.join(',')) ? (
                    <Badge colorScheme="purple" fontSize="sm" p={2} borderRadius="md">
                      Pointers B–D / Code Set Updated
                    </Badge>
                  ) : (v1Codes.length > 0 && v3Codes.length > 0) ? (
                    <Badge colorScheme="gray" fontSize="sm" p={2} borderRadius="md">
                      Identical Sequence ({v1PointerA})
                    </Badge>
                  ) : null}
                </Box>
              </SimpleGrid>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={8}>

            {/* Left Column: Current (NMA_ADVANCED_IONM) */}
            <Box bg="white" p={4} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200">
              <VStack align="stretch" spacing={4}>
                <Box borderBottom="2px solid" borderColor="gray.100" pb={2}>
                  <Heading size="md" color="gray.600">Current (Advanced)</Heading>
                  <Text fontSize="sm" color="gray.500">NMA_ADVANCED_IONM Pipeline ({claimType.toUpperCase()})</Text>
                </Box>

                {v1Results ? (
                  <>
                    <CptLinePointersCard
                      results={v1Results}
                      compareResults={v3Results}
                      claimsData={claimsData}
                      claimType={claimType}
                      accentScheme="blue"
                    />
                    <MedicalNecessityPanel results={v1Results} highlightCodes={sharedCodes} />
                  </>
                ) : (
                  <Alert status="warning">
                    <AlertIcon />
                    No Current (Advanced) results found for this case ({claimType.toUpperCase()}).
                  </Alert>
                )}
              </VStack>
            </Box>

            {/* Right Column: V3 Pipeline (NMA_V3_IONM) */}
            <Box bg="white" p={4} borderRadius="lg" shadow="sm" border="2px solid" borderColor="purple.200">
              <VStack align="stretch" spacing={4}>
                <Box borderBottom="2px solid" borderColor="purple.100" pb={2}>
                  <HStack justify="space-between">
                    <Box>
                      <Heading size="md" color="purple.700">V3 (Beta)</Heading>
                      <Text fontSize="sm" color="gray.500">NMA_V3_IONM Pipeline ({claimType.toUpperCase()} — V3 Crosswalk + 5-Tier Sequencing)</Text>
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant={v3Results ? 'outline' : 'solid'}
                      onClick={handleRunV3Analysis}
                      isLoading={runningV3}
                      loadingText="Running V3..."
                    >
                      {v3Results ? 'Re-Run V3' : 'Run V3 Analysis'}
                    </Button>
                  </HStack>
                </Box>

                {v3Results ? (
                  <>
                    <CptLinePointersCard
                      results={v3Results}
                      compareResults={v1Results}
                      claimsData={claimsData}
                      claimType={claimType}
                      accentScheme="purple"
                    />
                    <MedicalNecessityPanel results={v3Results} highlightCodes={sharedCodes} />
                  </>
                ) : (
                  <Alert status="warning">
                    <AlertIcon />
                    No V3 results found for this case ({claimType.toUpperCase()}) yet. Click &quot;Run V3 Analysis&quot; above to generate V3 output.
                  </Alert>
                )}
              </VStack>
            </Box>

          </SimpleGrid>

          {/* LLM Comparison Section */}
          <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px solid" borderColor="gray.200" mt={8}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Heading size="md" color="gray.700">LLM Auditor Comparison (Advanced vs V3)</Heading>
                {!comparisonData && (
                  <Button
                    colorScheme="purple"
                    onClick={handleGenerateComparison}
                    isLoading={generatingComparison}
                    isDisabled={!v1Results || !v3Results}
                    loadingText="Analyzing..."
                  >
                    Generate LLM Comparison
                  </Button>
                )}
              </HStack>

              {comparisonError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {comparisonError}
                </Alert>
              )}

              {comparisonData && llmAnalysis && (
                <VStack align="stretch" spacing={6} mt={4}>
                  <HStack justify="space-between" p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                    <Box>
                      <Text fontWeight="bold" color="gray.600">Winner</Text>
                      <Badge
                        colorScheme={(llmAnalysis.winner === 'V3' || llmAnalysis.winner === 'V2') ? 'purple' : llmAnalysis.winner === 'V1' ? 'blue' : 'gray'}
                        fontSize="lg"
                        p={2}
                        mt={1}
                      >
                        {llmAnalysis.winner === 'V2' ? 'V3' : llmAnalysis.winner}
                      </Badge>
                    </Box>
                    <Box textAlign="right">
                      <Text fontWeight="bold" color="gray.600">V3 Preference Score</Text>
                      <Text fontSize="2xl" fontWeight="bold" color={preferenceScore >= 60 ? 'green.500' : 'red.500'}>
                        {preferenceScore} / 100
                      </Text>
                    </Box>
                  </HStack>

                  <Box>
                    <Text fontWeight="bold" mb={2}>Clinical Baseline</Text>
                    <Text color="gray.700">{llmAnalysis.clinical_baseline}</Text>
                  </Box>

                  <Divider />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                      <Text fontWeight="bold" color="blue.600" mb={2}>Current (Advanced) Evaluation</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Necessity &amp; Sequencing Strength:</Text>
                      <Text fontSize="sm" color="gray.700">{llmAnalysis.v1_evaluation?.necessity_strength}</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Defensibility:</Text>
                      <Text fontSize="sm" color="gray.700">{llmAnalysis.v1_evaluation?.defensibility}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" color="purple.600" mb={2}>V3 Evaluation</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Necessity &amp; Sequencing Strength:</Text>
                      <Text fontSize="sm" color="gray.700">{v3Eval?.necessity_strength}</Text>
                      <Text fontSize="sm" fontWeight="bold" mt={2}>Defensibility:</Text>
                      <Text fontSize="sm" color="gray.700">{v3Eval?.defensibility}</Text>
                    </Box>
                  </SimpleGrid>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>Comparison Synthesis</Text>
                    <Text color="gray.700">{llmAnalysis.comparison_synthesis}</Text>
                  </Box>

                  <HStack justify="flex-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleGenerateComparison}
                      isLoading={generatingComparison}
                    >
                      Regenerate
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Box>

        </VStack>
      </Box>
    </Box>
  );
};

export default NMAV2Compare;
